import { logger } from "../utils/logger";
/**
 * API Configuration
 * Central location for all API URLs - PRODUCTION READY
 * 
 * ⚠️ DEPLOYMENT GUIDE:
 * - For staging: Uses 'new.creabiz.co'
 * - For production: Uses 'www.creabiz.co'
 * - Automatically detects environment
 */


// ============================================
// 🌍 ENVIRONMENT DETECTION
// ============================================

/**
 * Detect current environment based on hostname
 */
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  if (typeof window === 'undefined') return 'staging';
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname.includes('figma')) {
    return 'development';
  }
  
  if (hostname.includes('www.creabiz.co')) {
    return 'staging';
  }
  
  // Production: www.creabiz.co or creabiz.co
  return 'production';
};

/**
 * Get the base domain for current environment
 */
const getBaseDomain = (): string => {
  // FORCE PRODUCTION - Site is live at www.creabiz.co
  return 'https://www.creabiz.co';
  
  /* Environment detection disabled - uncomment if needed
  const env = getEnvironment();
  
  switch (env) {
    case 'development':
      return 'https://new.creabiz.co';
    case 'staging':
      return 'https://new.creabiz.co';
    case 'production':
      return 'https://www.creabiz.co';
    default:
      return 'https://new.creabiz.co';
  }
  */
};

// ============================================
// 📡 API CONFIGURATION
// ============================================

/**
 * Base domain - automatically switches based on environment
 */
export const BASE_DOMAIN = getBaseDomain();

/**
 * Main API base URL
 * ✅ INCLUDES /api/ PREFIX - Required for RunCloud backend routing
 */
export const API_BASE_URL = `${BASE_DOMAIN}/api/make-server-e799d0d8`;

/**
 * Legacy export for backward compatibility
 */
export const API_URL = API_BASE_URL;

/**
 * Current environment
 */
export const ENVIRONMENT = getEnvironment();

// ============================================
// 🔧 HELPER FUNCTIONS
// ============================================

/**
 * Build full API URL from path
 * @param path - API endpoint path (e.g., '/blogs' or 'blogs')
 * @returns Full API URL
 */
export const getApiUrl = (path?: string): string => {
  if (!path) return API_BASE_URL;
  
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_BASE_URL}/${cleanPath}`;
};

/**
 * Get API headers with optional authentication
 * @param includeAuth - Whether to include auth token
 * @param accessToken - Optional specific token to use
 * @returns Headers object
 */
export const getApiHeaders = (includeAuth = true, accessToken?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = accessToken || (typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

/**

 * Get the public-facing base URL (for SEO, canonical URLs, sitemaps, etc.)

 * @param path - URL path (e.g., '/blog/post-slug' or 'blog/post-slug')

 * @returns Full public URL

 */

export const getPublicUrl = (path: string = ''): string => {

  // Ensure path starts with /

  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${BASE_DOMAIN}${cleanPath}`;

};

// ============================================
// 🎨 MEDIA URL HELPERS
// ============================================

/**
 * Convert internal media URL to branded public URL
 * Example: /api/make-server-e799d0d8/uploads/images/file.jpg -> https://www.creabiz.co/media/images/file.jpg
 */
export const getBrandedMediaUrl = (internalUrl: string): string => {
  if (!internalUrl) return '';
  
  // Extract the path after /uploads/
  const uploadsIndex = internalUrl.indexOf('/uploads/');
  if (uploadsIndex === -1) return `${BASE_DOMAIN}${internalUrl}`;
  
  const pathAfterUploads = internalUrl.substring(uploadsIndex + '/uploads/'.length);
  return `${BASE_DOMAIN}/media/${pathAfterUploads}`;
};

/**
 * Convert branded URL back to internal API URL
 * Example: https://www.creabiz.co/media/images/file.jpg -> /api/make-server-e799d0d8/uploads/images/file.jpg
 */
export const getInternalMediaUrl = (brandedUrl: string): string => {
  if (!brandedUrl) return '';
  
  // If it's already an internal URL, return as-is
  if (brandedUrl.includes('/api/make-server-e799d0d8/') || brandedUrl.includes('/make-server-e799d0d8/')) return brandedUrl;
  
  // Extract path after /media/
  const mediaIndex = brandedUrl.indexOf('/media/');
  if (mediaIndex === -1) return brandedUrl;
  
  const pathAfterMedia = brandedUrl.substring(mediaIndex + '/media/'.length);
  return `/api/make-server-e799d0d8/uploads/${pathAfterMedia}`;
};

// ============================================
// 🔐 AUTHENTICATED FETCH WRAPPER
// ============================================

/**
 * Enhanced fetch wrapper with automatic 401 handling
 * Use this instead of raw fetch() for authenticated requests
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const response = await fetch(url, options);
  
  // Global 401 handler
  if (response.status === 401) {
    logger.warn('🚨 [authFetch] 401 Unauthorized - Session expired');
    
    // Clear the invalid token
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
      
      // Only redirect if we're not already on the login page
      const currentPath = window.location.hash || window.location.pathname;
      if (!currentPath.includes('/admin/login') && !currentPath.includes('/admin/signup')) {
        toast.error('Session expired. Please log in again.', {
          duration: 4000,
        });
        
        setTimeout(() => {
          window.location.hash = '#/admin/login';
        }, 500);
      }
    }
  }
  
  return response;
};

// ============================================
// 📊 LOGGING (Development Only)
// ============================================

if (typeof window !== 'undefined' && ENVIRONMENT === 'development') {

}